import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBlocker, useLocation, useNavigate } from 'react-router-dom';
import { Leaf, Pencil, Plus, Star } from 'lucide-react';
import { RitualEntryForm, type RitualEntryFormHandle, type RitualFormValue } from '../components/RitualEntryForm';
import { EmptyState, PrimaryButton } from '../components/ui';
import { cycleModalTab, synchronizeModalFocus } from '../lib/modalFocus';
import { journalStore, type JournalEntry } from '../lib/journalStore';
import { draftFromEntry, emptyRitualAllocation, localDateTimeValue, ritualTotals, type RitualDraft } from '../lib/ritualModel';
import { attemptRitualPersistence, continueRitualIntent, protectRitualBeforeUnload, requestRitualIntent, resolveRitualDecision, type RitualPendingIntent, type RitualPersistenceResult } from '../lib/ritualUnsavedChanges';

export interface RitualNavigationState { ritualDraft?: RitualDraft }

function newDraft(prefill?: RitualDraft): RitualDraft {
  return prefill ?? { title: '', occurredAt: localDateTimeValue(), allocations: [emptyRitualAllocation()], allowAllocationlessLegacySave: false, targetMode: 'weight', mood: '', effects: '', notes: '', blendLabel: '', origin: 'manual' };
}

function consumptionSummary(entry: JournalEntry): string {
  if (entry.allocations.length === 0) return entry.blendLabel;
  const rows = entry.allocations.map((snapshot) => ({ product: null, snapshot, weightInput: String(snapshot.weightGrams), doseInput: String(snapshot.thcDoseMg) }));
  const totals = ritualTotals(rows, entry.targetMode);
  const first = entry.allocations[0];
  const name = entry.allocations.length === 1
    ? [first?.brand, first?.productName].filter(Boolean).join(' - ')
    : `${entry.allocations.length}-strain blend`;
  return `${name} · ${totals.totalWeightGrams.toFixed(2)} g · ${totals.totalThcDoseMg.toFixed(2)} mg THC`;
}

export function Journal(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const prefill = (location.state as RitualNavigationState | null)?.ritualDraft;
  const [entries, setEntries] = useState(() => journalStore.list());
  const [draft, setDraft] = useState<RitualDraft | null>(() => prefill ? newDraft(prefill) : null);
  const [editing, setEditing] = useState<JournalEntry | null>(null);
  const [dirty, setDirty] = useState(false);
  const [pending, setPending] = useState<RitualPendingIntent<JournalEntry> | null>(null);
  const formRef = useRef<RitualEntryFormHandle>(null);
  const formHeadingRef = useRef<HTMLHeadingElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const dialogSaveRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const modalFocusState = useRef({ wasOpen: false });
  const refresh = (): void => setEntries(journalStore.list());

  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    dirty && currentLocation.pathname !== nextLocation.pathname,
  );

  const scrollToForm = useCallback((): void => {
    window.requestAnimationFrame(() => {
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
      formHeadingRef.current?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
      formHeadingRef.current?.focus({ preventScroll: true });
    });
  }, []);

  const close = useCallback((): void => {
    setDraft(null);
    setEditing(null);
    setDirty(false);
    if (prefill) navigate('/rituals', { replace: true, state: null });
  }, [navigate, prefill]);

  const loadEntry = useCallback((entry: JournalEntry): void => {
    setEditing(entry);
    setDraft(null);
    setDirty(false);
    scrollToForm();
  }, [scrollToForm]);

  const finishIntent = useCallback((intent: RitualPendingIntent<JournalEntry>): void => {
    setPending(null);
    continueRitualIntent(intent, { load: loadEntry, close, navigate: () => blocker.proceed?.() });
  }, [blocker, close, loadEntry]);

  const save = (value: RitualFormValue): RitualPersistenceResult => {
    return attemptRitualPersistence(value, (saveValue) => editing
      ? journalStore.update(editing.id, saveValue) !== null
      : Boolean(journalStore.create({ ...saveValue, favourite: false })));
  };

  const finishSave = (): void => {
    refresh();
    if (pending) finishIntent(pending); else close();
  };

  const requestIntent = (intent: RitualPendingIntent<JournalEntry>): void => {
    const request = requestRitualIntent(dirty, intent);
    if (request.continueWith) { finishIntent(request.continueWith); return; }
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    setPending(request.pending);
  };

  useEffect(() => {
    if (blocker.state !== 'blocked' || pending) return;
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    setPending({ kind: 'navigate' });
  }, [blocker.state, pending]);

  useEffect(() => {
    return protectRitualBeforeUnload(window, dirty);
  }, [dirty]);

  useEffect(() => {
    synchronizeModalFocus(Boolean(pending), modalFocusState.current, dialogSaveRef.current, returnFocusRef.current);
  }, [pending]);

  const discardPending = (): void => {
    if (!pending) return;
    const decision = resolveRitualDecision(pending, 'discard');
    if (decision.clearDirty) setDirty(false);
    if (decision.continueWith) finishIntent(decision.continueWith);
  };

  const cancelPending = (): void => {
    if (!pending) return;
    const decision = resolveRitualDecision(pending, 'cancel');
    setPending(null);
    if (decision.cancelBlockedNavigation && blocker.state === 'blocked') blocker.reset();
  };

  const savePending = (): void => {
    if (formRef.current?.submit()) return;
    if (!pending) return;
    const decision = resolveRitualDecision(pending, 'save-failed');
    setPending(null);
    if (decision.cancelBlockedNavigation && blocker.state === 'blocked') blocker.reset();
    window.requestAnimationFrame(() => formHeadingRef.current?.focus());
  };

  const editEntry = (entry: JournalEntry): void => {
    if (editing?.id === entry.id) { scrollToForm(); return; }
    requestIntent({ kind: 'switch', value: entry });
  };

  const cancelForm = (): void => requestIntent({ kind: 'close' });
  const sorted = useMemo(() => entries, [entries]);
  const toggleFav = (entry: JournalEntry): void => { journalStore.update(entry.id, { favourite: !entry.favourite }); refresh(); };
  const remove = (id: string): void => { if (!window.confirm('Delete this Ritual?')) return; journalStore.remove(id); refresh(); };
  const formOpen = Boolean(draft || editing);

  return <div className="stack">
    <div><h1 className="page-title">Rituals <Leaf className="page-title__leaf" size={22} aria-hidden /></h1><p className="page-lead">Record what you consumed now, then return to add reflections later.</p></div>
    {formOpen ? <section className="ritual-active-section" aria-labelledby="ritual-form-heading">
      <h2 id="ritual-form-heading" ref={formHeadingRef} tabIndex={-1} className="mt-0">{editing ? 'Edit Ritual' : 'New Ritual'}</h2>
      <RitualEntryForm key={editing?.id ?? 'new'} ref={formRef} initial={editing ? draftFromEntry(editing) : draft!} onSave={save} onSaveSuccess={finishSave} onCancel={cancelForm} onDirtyChange={setDirty} />
    </section> : null}

    {sorted.length === 0 && !formOpen ? <EmptyState title="No Rituals yet" description="Record a strain or blend, its weight and THC dose, then reflect whenever you are ready." action={<PrimaryButton icon={<Plus size={18} />} onClick={() => setDraft(newDraft())}>New Ritual</PrimaryButton>} /> : null}

    {sorted.length > 0 ? <section className="saved-rituals-section" aria-labelledby="saved-rituals-heading">
      <h2 id="saved-rituals-heading" className="saved-rituals-section__heading">Saved Rituals</h2>
      <div className="stack" style={{ gap: '0.75rem' }}>{sorted.map((entry) => <article key={entry.id} className="card journal-card">
        <div className="spread" style={{ alignItems: 'flex-start' }}><div><h3 className="journal-card__title">{entry.title}</h3><div className="muted" style={{ fontSize: '0.85rem' }}>{new Date(entry.occurredAt).toLocaleString()}</div>{consumptionSummary(entry) ? <div className="ritual-card__consumption">{consumptionSummary(entry)}</div> : null}</div>
        <button type="button" className="icon-btn" aria-label={entry.favourite ? 'Unfavourite Ritual' : 'Favourite Ritual'} onClick={() => toggleFav(entry)}><Star size={18} fill={entry.favourite ? 'var(--color-star)' : 'none'} color={entry.favourite ? 'var(--color-star)' : 'currentColor'} /></button></div>
        {(entry.mood || entry.effects) ? <div className="row">{entry.mood ? <span className="info-chip">{entry.mood}</span> : null}{entry.effects ? <span className="info-chip info-chip--sage">{entry.effects}</span> : null}</div> : null}
        {entry.notes ? <p className="journal-card__notes">{entry.notes}</p> : null}
        <div className="row"><button type="button" className="btn btn-secondary" onClick={() => editEntry(entry)}><Pencil size={16} aria-hidden /> Edit Ritual</button><button type="button" className="btn btn-ghost" onClick={() => remove(entry.id)}>Delete</button></div>
      </article>)}</div>
    </section> : null}

    {!formOpen && sorted.length > 0 ? <button type="button" className="fab" aria-label="New Ritual" onClick={() => setDraft(newDraft())}><Plus size={22} /></button> : null}

    {pending ? <div className="product-form-modal" role="presentation">
      <div ref={dialogRef} className="product-form-modal__dialog stack ritual-unsaved-dialog" role="alertdialog" aria-modal="true" aria-labelledby="ritual-unsaved-title" onKeyDown={(event) => {
        if (event.key === 'Escape') { event.preventDefault(); cancelPending(); return; }
        if (event.key !== 'Tab') return;
        const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])') ?? []);
        cycleModalTab(event, focusable, document.activeElement as HTMLElement | null);
      }}>
        <h2 id="ritual-unsaved-title" className="mt-0">You have unsaved changes.</h2>
        <div className="row ritual-unsaved-dialog__actions">
          <button ref={dialogSaveRef} type="button" className="btn btn-primary" onClick={savePending}>Save Changes</button>
          <button type="button" className="btn btn-secondary" onClick={discardPending}>Discard Changes</button>
          <button type="button" className="btn btn-ghost" onClick={cancelPending}>Cancel</button>
        </div>
      </div>
    </div> : null}
  </div>;
}
